import type { MutableRefObject } from 'react'

const RECONNECT_BASE_DELAY_MS = 1000
const RECONNECT_MAX_DELAY_MS = 10000

export interface WebSocketLifecycleRefs {
  socketRef: MutableRefObject<WebSocket | null>
  reconnectTimeoutRef: MutableRefObject<number | null>
  reconnectAttemptRef: MutableRefObject<number>
  intentionalCloseRef: MutableRefObject<boolean>
  isMountedRef: MutableRefObject<boolean>
}

/**
 * Return whether the provided socket is still active enough to block a new connect attempt.
 *
 * @param socket - The current WebSocket instance, if any.
 * @returns True when the socket is connecting, open, or closing.
 */
export const isSocketConnectionActive = (socket: WebSocket | null): boolean => (
  socket?.readyState === WebSocket.CONNECTING
  || socket?.readyState === WebSocket.OPEN
  || socket?.readyState === WebSocket.CLOSING
)

/**
 * Clear a scheduled reconnect timer when one exists.
 *
 * @param reconnectTimeoutRef - Mutable ref that stores the reconnect timer id.
 * @returns void
 */
export const clearScheduledReconnect = (
  reconnectTimeoutRef: MutableRefObject<number | null>
): void => {
  if (reconnectTimeoutRef.current === null) {
    return
  }

  window.clearTimeout(reconnectTimeoutRef.current)
  reconnectTimeoutRef.current = null
}

/**
 * Prepare lifecycle refs for a new outbound WebSocket connection attempt.
 *
 * @param refs - Shared lifecycle refs for the managed socket.
 * @returns True when a new socket should be opened, false when an existing socket is still active or unmounted.
 */
export const prepareWebSocketConnect = (refs: WebSocketLifecycleRefs): boolean => {
  clearScheduledReconnect(refs.reconnectTimeoutRef)

  if (!refs.isMountedRef.current) {
    return false
  }

  if (isSocketConnectionActive(refs.socketRef.current)) {
    return false
  }

  refs.intentionalCloseRef.current = false

  if (refs.socketRef.current?.readyState === WebSocket.CLOSED) {
    refs.socketRef.current = null
  }

  return true
}

/**
 * Reset reconnect state after a socket has opened successfully.
 *
 * @param reconnectAttemptRef - Mutable ref that stores the reconnect attempt count.
 * @param reconnectTimeoutRef - Mutable ref that stores the reconnect timer id.
 * @returns void
 */
export const markWebSocketOpened = (
  reconnectAttemptRef: MutableRefObject<number>,
  reconnectTimeoutRef: MutableRefObject<number | null>
): void => {
  reconnectAttemptRef.current = 0
  clearScheduledReconnect(reconnectTimeoutRef)
}

/**
 * Flag that the current shutdown is intentional and stop any pending reconnect.
 *
 * @param intentionalCloseRef - Mutable ref tracking whether the close was intentional.
 * @param reconnectTimeoutRef - Mutable ref that stores the reconnect timer id.
 * @returns void
 */
export const beginIntentionalClose = (
  intentionalCloseRef: MutableRefObject<boolean>,
  reconnectTimeoutRef: MutableRefObject<number | null>
): void => {
  intentionalCloseRef.current = true
  clearScheduledReconnect(reconnectTimeoutRef)
}

/**
 * Clear the active socket ref when the close event belongs to the current socket.
 *
 * @param socketRef - Mutable ref that stores the live WebSocket instance.
 * @param socket - Socket instance associated with the close event.
 * @returns void
 */
export const clearSocketReference = (
  socketRef: MutableRefObject<WebSocket | null>,
  socket: WebSocket
): void => {
  if (socketRef.current === socket) {
    socketRef.current = null
  }
}

/**
 * Compute the reconnect delay for the next attempt using capped exponential backoff.
 *
 * @param attempt - Zero-based reconnect attempt number.
 * @returns The reconnect delay in milliseconds.
 */
export const getReconnectDelayMs = (attempt: number): number => (
  Math.min(RECONNECT_BASE_DELAY_MS * (2 ** attempt), RECONNECT_MAX_DELAY_MS)
)

/**
 * Schedule a reconnect attempt when the hook is still mounted and the close was not intentional.
 *
 * @param refs - Shared lifecycle refs for the managed socket.
 * @param reconnect - Callback that opens a new socket.
 * @returns The scheduled delay in milliseconds, or null when no reconnect was scheduled.
 */
export const scheduleReconnect = (
  refs: WebSocketLifecycleRefs,
  reconnect: () => void
): number | null => {
  if (!refs.isMountedRef.current || refs.intentionalCloseRef.current) {
    return null
  }

  const delayMs = getReconnectDelayMs(refs.reconnectAttemptRef.current)
  refs.reconnectAttemptRef.current += 1

  clearScheduledReconnect(refs.reconnectTimeoutRef)
  refs.reconnectTimeoutRef.current = window.setTimeout(() => {
    refs.reconnectTimeoutRef.current = null

    if (!refs.isMountedRef.current || refs.intentionalCloseRef.current) {
      return
    }

    reconnect()
  }, delayMs)

  return delayMs
}

/**
 * Build a stable fallback message for websocket opens that fail before `onopen`.
 *
 * @param connectionLabel - User-facing label describing the rejected connection.
 * @returns A retry-oriented error message for the UI.
 */
export const getPreOpenRejectionMessage = (connectionLabel: string): string => (
  `The server rejected the ${connectionLabel} before it opened. `
  + 'Close other advisor tabs, wait about 90 seconds, and retry.'
)

/**
 * Return user-facing copy for websocket close codes and backend close reasons.
 *
 * @param code - WebSocket close code supplied by the server.
 * @param reason - Optional backend close reason.
 * @returns A retry-oriented error message, or null when the close should use normal reconnect handling.
 */
export const getWebSocketCloseError = (code: number, reason = ''): string | null => {
  if (code === 4401) return 'Authentication required. Please sign in again.'
  if (code === 4408) return 'Message too large for realtime processing.'

  if (code !== 4429) {
    return null
  }

  if (reason === 'connection_rate_limit') {
    return 'Too many realtime connection attempts. Wait about 60 seconds, then retry.'
  }

  if (reason === 'connection_concurrency_limit') {
    return 'Too many realtime tabs are already connected. Close other advisor tabs or wait about 90 seconds, then retry.'
  }

  if (reason === 'connection_idle_timeout') {
    return 'Realtime connection went idle. Retry when you are ready.'
  }

  if (reason === 'rate_limited') {
    return 'Too many realtime messages were sent. Please slow down and retry.'
  }

  return 'Realtime connection limit reached. Close duplicate tabs, wait about 60 seconds, and retry.'
}

const KEEPALIVE_INTERVAL_MS = 25000

/**
 * Begin sending periodic keepalive pings so the server can tell a live-but-quiet
 * client from a dead one. Without this, a connection that stays open while the
 * user reads (sending nothing) would look idle to the server's reaper.
 *
 * @param socketRef - Ref holding the active socket to ping.
 * @returns The interval id, for later cancellation via stopKeepAlive.
 */
export const startKeepAlive = (
  socketRef: MutableRefObject<WebSocket | null>
): number => window.setInterval(() => {
  const socket = socketRef.current
  if (socket?.readyState === WebSocket.OPEN) {
    try {
      socket.send(JSON.stringify({ type: 'ping' }))
    } catch {
      // Transient send failures are recovered by the socket's close handler.
    }
  }
}, KEEPALIVE_INTERVAL_MS)

/**
 * Cancel a keepalive interval started by startKeepAlive and clear the ref.
 *
 * @param intervalRef - Ref holding the interval id, or null when inactive.
 * @returns void
 */
export const stopKeepAlive = (intervalRef: MutableRefObject<number | null>): void => {
  if (intervalRef.current !== null) {
    window.clearInterval(intervalRef.current)
    intervalRef.current = null
  }
}

/**
 * Close the socket on pagehide so the server frees the connection slot
 * immediately instead of waiting out the idle timeout. Helps the common case of
 * closing or refreshing a tab; hard network drops are handled by the server reaper.
 *
 * @param socketRef - Ref holding the active socket to close.
 * @returns A cleanup function that removes the listener.
 */
export const registerSlotReleaseOnPageHide = (
  socketRef: MutableRefObject<WebSocket | null>
): (() => void) => {
  const handler = (): void => {
    const socket = socketRef.current
    if (socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) {
      try {
        socket.close(1000, 'page-hidden')
      } catch {
        // Best-effort release; the server reaper covers any close that fails to land.
      }
    }
  }

  window.addEventListener('pagehide', handler)
  return () => window.removeEventListener('pagehide', handler)
}
